/**
 * "You searched Mumbai; this flight leaves from Navi Mumbai."
 *
 * A metro search returns flights from every airport the supplier files under
 * that city, and a traveller who asked for BOM can be handed a 06:00 from NMI
 * without the card ever saying so - the city line reads "Navi Mumbai" in the
 * same quiet grey as everything else. The big OTAs flag this on the result
 * ("35 km from Mumbai") and again at review, and that is what these helpers
 * feed.
 *
 * **The kilometres are straight-line, airport to airport.** That is what the
 * big booking sites show (Goibibo's "Navi Mumbai (25Km)", Trip.com's computed
 * "21.5499 km", Kayak's "radius"), it is one definition that holds for every
 * pair in the world, and it is what a traveller comparing us with them expects
 * to see. It does understate the trip - Mumbai to Navi Mumbai is 25 km on the
 * map and 40-odd by road across the creek - which is why the filter rail adds
 * an approximate drive time beside it: the honest answer to "how far" is
 * minutes, and the sites that answer it well (Trip.com) answer it that way.
 *
 * The table is curated, not computed at run time: the City master's
 * coordinates are city centres, not airports, and are not reliably filled. The
 * numbers were computed once from airport coordinates and rounded, except
 * where a reference site publishes one (NMI 25, DXN 71), which is kept as
 * published. A pair that is not listed still gets the flag, just without the
 * kilometres.
 */

import { AIRPORT_NAMES } from './airport-names.js';

interface AlternateAirport {
  /** The secondary airport. */
  code: string;
  city: string;
  /** The metro's main airport, the one a city search normally means. */
  hub: string;
  hubCity: string;
  /** Straight-line distance between the two airports, rounded. */
  distanceKm: number;
  /**
   * Roughly how long the drive between them takes in ordinary traffic. An
   * estimate, always shown with a "~": it is here to say "an hour, not ten
   * minutes", not to plan a connection by.
   */
  driveMinutes: number;
}

const ALTERNATE_AIRPORTS: readonly AlternateAirport[] = [
  // TBO answers a metro search with every airport it files under that city -
  // a DEL search brings Noida and Hindon, BOM brings Navi Mumbai, DXB brings
  // Sharjah (all seen live, 2026-09-21).
  {
    code: 'NMI',
    city: 'Navi Mumbai',
    hub: 'BOM',
    hubCity: 'Mumbai',
    distanceKm: 25,
    driveMinutes: 60,
  },
  { code: 'DXN', city: 'Noida', hub: 'DEL', hubCity: 'Delhi', distanceKm: 71, driveMinutes: 90 },
  {
    code: 'HDO',
    city: 'Hindon (Ghaziabad)',
    hub: 'DEL',
    hubCity: 'Delhi',
    distanceKm: 29,
    driveMinutes: 60,
  },
  {
    code: 'GOX',
    city: 'Mopa',
    hub: 'GOI',
    hubCity: 'Goa (Dabolim)',
    distanceKm: 39,
    driveMinutes: 90,
  },
  {
    code: 'DWC',
    city: 'Dubai Al Maktoum',
    hub: 'DXB',
    hubCity: 'Dubai',
    distanceKm: 45,
    driveMinutes: 45,
  },
  { code: 'SHJ', city: 'Sharjah', hub: 'DXB', hubCity: 'Dubai', distanceKm: 17, driveMinutes: 30 },
  // Etihad files a Dubai search under its Abu Dhabi hub and its own coach
  // terminal (XNB, Sheikh Zayed Road near Equiti metro) - both seen live on a
  // BOM-DXB search, 2026-09-23. XNB's distance is from its street address; no
  // airport database carries better than city-level coordinates for it.
  {
    code: 'XNB',
    city: 'Dubai Bus Station',
    hub: 'DXB',
    hubCity: 'Dubai',
    distanceKm: 18,
    driveMinutes: 30,
  },
  {
    code: 'AUH',
    city: 'Abu Dhabi',
    hub: 'DXB',
    hubCity: 'Dubai',
    distanceKm: 116,
    driveMinutes: 90,
  },
  {
    code: 'SAW',
    city: 'Istanbul Sabiha Gökçen',
    hub: 'IST',
    hubCity: 'Istanbul',
    distanceKm: 63,
    driveMinutes: 75,
  },
  {
    code: 'THR',
    city: 'Tehran Mehrabad',
    hub: 'IKA',
    hubCity: 'Tehran Imam Khomeini',
    distanceKm: 34,
    driveMinutes: 50,
  },
  {
    code: 'DMK',
    city: 'Bangkok Don Mueang',
    hub: 'BKK',
    hubCity: 'Bangkok',
    distanceKm: 29,
    driveMinutes: 40,
  },
  {
    code: 'SZB',
    city: 'Subang',
    hub: 'KUL',
    hubCity: 'Kuala Lumpur',
    distanceKm: 46,
    driveMinutes: 50,
  },
  {
    code: 'HLP',
    city: 'Jakarta Halim',
    hub: 'CGK',
    hubCity: 'Jakarta Soekarno-Hatta',
    distanceKm: 30,
    driveMinutes: 50,
  },
  { code: 'CRK', city: 'Clark', hub: 'MNL', hubCity: 'Manila', distanceKm: 90, driveMinutes: 90 },
  {
    code: 'RML',
    city: 'Colombo Ratmalana',
    hub: 'CMB',
    hubCity: 'Colombo',
    distanceKm: 40,
    driveMinutes: 60,
  },
  {
    code: 'NRT',
    city: 'Tokyo Narita',
    hub: 'HND',
    hubCity: 'Tokyo Haneda',
    distanceKm: 61,
    driveMinutes: 70,
  },
  {
    code: 'ITM',
    city: 'Osaka Itami',
    hub: 'KIX',
    hubCity: 'Osaka Kansai',
    distanceKm: 44,
    driveMinutes: 60,
  },
  {
    code: 'UKB',
    city: 'Kobe',
    hub: 'KIX',
    hubCity: 'Osaka Kansai',
    distanceKm: 22,
    driveMinutes: 70,
  },
  {
    code: 'GMP',
    city: 'Seoul Gimpo',
    hub: 'ICN',
    hubCity: 'Seoul Incheon',
    distanceKm: 33,
    driveMinutes: 35,
  },
  {
    code: 'PKX',
    city: 'Beijing Daxing',
    hub: 'PEK',
    hubCity: 'Beijing Capital',
    distanceKm: 65,
    driveMinutes: 75,
  },
  {
    code: 'SHA',
    city: 'Shanghai Hongqiao',
    hub: 'PVG',
    hubCity: 'Shanghai Pudong',
    distanceKm: 45,
    driveMinutes: 55,
  },
  {
    code: 'TSA',
    city: 'Taipei Songshan',
    hub: 'TPE',
    hubCity: 'Taipei Taoyuan',
    distanceKm: 32,
    driveMinutes: 40,
  },
  {
    code: 'AVV',
    city: 'Avalon',
    hub: 'MEL',
    hubCity: 'Melbourne',
    distanceKm: 53,
    driveMinutes: 45,
  },
  {
    code: 'LGW',
    city: 'London Gatwick',
    hub: 'LHR',
    hubCity: 'London Heathrow',
    distanceKm: 40,
    driveMinutes: 55,
  },
  {
    code: 'STN',
    city: 'London Stansted',
    hub: 'LHR',
    hubCity: 'London Heathrow',
    distanceKm: 67,
    driveMinutes: 80,
  },
  {
    code: 'LTN',
    city: 'London Luton',
    hub: 'LHR',
    hubCity: 'London Heathrow',
    distanceKm: 45,
    driveMinutes: 50,
  },
  {
    code: 'LCY',
    city: 'London City',
    hub: 'LHR',
    hubCity: 'London Heathrow',
    distanceKm: 35,
    driveMinutes: 70,
  },
  {
    code: 'SEN',
    city: 'London Southend',
    hub: 'LHR',
    hubCity: 'London Heathrow',
    distanceKm: 80,
    driveMinutes: 100,
  },
  {
    code: 'ORY',
    city: 'Paris Orly',
    hub: 'CDG',
    hubCity: 'Paris CDG',
    distanceKm: 34,
    driveMinutes: 45,
  },
  {
    code: 'BVA',
    city: 'Paris Beauvais',
    hub: 'CDG',
    hubCity: 'Paris CDG',
    distanceKm: 59,
    driveMinutes: 75,
  },
  {
    code: 'LIN',
    city: 'Milan Linate',
    hub: 'MXP',
    hubCity: 'Milan Malpensa',
    distanceKm: 47,
    driveMinutes: 55,
  },
  {
    code: 'BGY',
    city: 'Milan Bergamo',
    hub: 'MXP',
    hubCity: 'Milan Malpensa',
    distanceKm: 76,
    driveMinutes: 65,
  },
  {
    code: 'CIA',
    city: 'Rome Ciampino',
    hub: 'FCO',
    hubCity: 'Rome Fiumicino',
    distanceKm: 30,
    driveMinutes: 35,
  },
  {
    code: 'CRL',
    city: 'Brussels Charleroi',
    hub: 'BRU',
    hubCity: 'Brussels',
    distanceKm: 49,
    driveMinutes: 50,
  },
  {
    code: 'HHN',
    city: 'Frankfurt Hahn',
    hub: 'FRA',
    hubCity: 'Frankfurt',
    distanceKm: 93,
    driveMinutes: 85,
  },
  {
    code: 'GRO',
    city: 'Girona',
    hub: 'BCN',
    hubCity: 'Barcelona',
    distanceKm: 88,
    driveMinutes: 70,
  },
  {
    code: 'TSF',
    city: 'Venice Treviso',
    hub: 'VCE',
    hubCity: 'Venice',
    distanceKm: 20,
    driveMinutes: 35,
  },
  {
    code: 'TRF',
    city: 'Oslo Torp',
    hub: 'OSL',
    hubCity: 'Oslo',
    distanceKm: 122,
    driveMinutes: 85,
  },
  {
    code: 'BMA',
    city: 'Stockholm Bromma',
    hub: 'ARN',
    hubCity: 'Stockholm Arlanda',
    distanceKm: 33,
    driveMinutes: 40,
  },
  {
    code: 'NYO',
    city: 'Stockholm Skavsta',
    hub: 'ARN',
    hubCity: 'Stockholm Arlanda',
    distanceKm: 112,
    driveMinutes: 80,
  },
  {
    code: 'DME',
    city: 'Moscow Domodedovo',
    hub: 'SVO',
    hubCity: 'Moscow Sheremetyevo',
    distanceKm: 70,
    driveMinutes: 90,
  },
  {
    code: 'VKO',
    city: 'Moscow Vnukovo',
    hub: 'SVO',
    hubCity: 'Moscow Sheremetyevo',
    distanceKm: 43,
    driveMinutes: 60,
  },
  {
    code: 'EWR',
    city: 'Newark',
    hub: 'JFK',
    hubCity: 'New York JFK',
    distanceKm: 34,
    driveMinutes: 60,
  },
  {
    code: 'LGA',
    city: 'New York LaGuardia',
    hub: 'JFK',
    hubCity: 'New York JFK',
    distanceKm: 17,
    driveMinutes: 25,
  },
  {
    code: 'MDW',
    city: 'Chicago Midway',
    hub: 'ORD',
    hubCity: "Chicago O'Hare",
    distanceKm: 24,
    driveMinutes: 45,
  },
  {
    code: 'DCA',
    city: 'Washington Reagan',
    hub: 'IAD',
    hubCity: 'Washington Dulles',
    distanceKm: 38,
    driveMinutes: 40,
  },
  {
    code: 'BWI',
    city: 'Baltimore',
    hub: 'IAD',
    hubCity: 'Washington Dulles',
    distanceKm: 72,
    driveMinutes: 70,
  },
  {
    code: 'HOU',
    city: 'Houston Hobby',
    hub: 'IAH',
    hubCity: 'Houston',
    distanceKm: 39,
    driveMinutes: 40,
  },
  {
    code: 'DAL',
    city: 'Dallas Love Field',
    hub: 'DFW',
    hubCity: 'Dallas Fort Worth',
    distanceKm: 19,
    driveMinutes: 25,
  },
  {
    code: 'FLL',
    city: 'Fort Lauderdale',
    hub: 'MIA',
    hubCity: 'Miami',
    distanceKm: 34,
    driveMinutes: 35,
  },
  {
    code: 'BUR',
    city: 'Burbank',
    hub: 'LAX',
    hubCity: 'Los Angeles',
    distanceKm: 29,
    driveMinutes: 40,
  },
  {
    code: 'LGB',
    city: 'Long Beach',
    hub: 'LAX',
    hubCity: 'Los Angeles',
    distanceKm: 27,
    driveMinutes: 35,
  },
  {
    code: 'SNA',
    city: 'Orange County',
    hub: 'LAX',
    hubCity: 'Los Angeles',
    distanceKm: 58,
    driveMinutes: 50,
  },
  {
    code: 'ONT',
    city: 'Ontario',
    hub: 'LAX',
    hubCity: 'Los Angeles',
    distanceKm: 76,
    driveMinutes: 60,
  },
  {
    code: 'OAK',
    city: 'Oakland',
    hub: 'SFO',
    hubCity: 'San Francisco',
    distanceKm: 17,
    driveMinutes: 35,
  },
  {
    code: 'SJC',
    city: 'San Jose',
    hub: 'SFO',
    hubCity: 'San Francisco',
    distanceKm: 49,
    driveMinutes: 40,
  },
  {
    code: 'YTZ',
    city: 'Toronto Billy Bishop',
    hub: 'YYZ',
    hubCity: 'Toronto Pearson',
    distanceKm: 19,
    driveMinutes: 35,
  },
  {
    code: 'YHU',
    city: 'Montreal Saint-Hubert',
    hub: 'YUL',
    hubCity: 'Montreal',
    distanceKm: 26,
    driveMinutes: 35,
  },
  {
    code: 'CGH',
    city: 'Sao Paulo Congonhas',
    hub: 'GRU',
    hubCity: 'Sao Paulo Guarulhos',
    distanceKm: 28,
    driveMinutes: 50,
  },
  {
    code: 'VCP',
    city: 'Campinas',
    hub: 'GRU',
    hubCity: 'Sao Paulo Guarulhos',
    distanceKm: 83,
    driveMinutes: 90,
  },
  {
    code: 'SDU',
    city: 'Rio Santos Dumont',
    hub: 'GIG',
    hubCity: 'Rio de Janeiro Galeão',
    distanceKm: 14,
    driveMinutes: 30,
  },
  {
    code: 'AEP',
    city: 'Buenos Aires Aeroparque',
    hub: 'EZE',
    hubCity: 'Buenos Aires Ezeiza',
    distanceKm: 31,
    driveMinutes: 45,
  },
];

export interface DifferentAirportNote {
  /** The airport the flight actually uses - its city as the supplier names it, and its code. */
  airport: string;
  code: string;
  /** The airport that was searched for. */
  searchedCode: string;
  /** Roughly how far apart the two are, when known. */
  distanceKm: number | null;
  /** The searched airport, named, when known ("Mumbai"). */
  from: string | null;
}

/**
 * The note to show when a flight uses a different airport from the one
 * searched, or null when it is the same one (or either side is unknown).
 */
export function differentAirportNote(
  searchedCode: string | null | undefined,
  actualCode: string | null | undefined,
  actualCity: string | null | undefined,
): DifferentAirportNote | null {
  const searched = searchedCode?.trim().toUpperCase();
  const actual = actualCode?.trim().toUpperCase();
  if (!searched || !actual || searched === actual) return null;

  const pair = ALTERNATE_AIRPORTS.find(
    (a) => (a.code === actual && a.hub === searched) || (a.code === searched && a.hub === actual),
  );
  const airport = actualCity?.trim() || (pair && pair.code === actual ? pair.city : null) || actual;
  if (!pair) return { airport, code: actual, searchedCode: searched, distanceKm: null, from: null };
  return {
    airport,
    code: actual,
    searchedCode: searched,
    distanceKm: pair.distanceKm,
    from: pair.code === actual ? pair.hubCity : pair.city,
  };
}

function findPair(
  a: string | null | undefined,
  b: string | null | undefined,
): AlternateAirport | undefined {
  const x = a?.trim().toUpperCase();
  const y = b?.trim().toUpperCase();
  if (!x || !y || x === y) return undefined;
  return ALTERNATE_AIRPORTS.find(
    (p) => (p.code === x && p.hub === y) || (p.code === y && p.hub === x),
  );
}

/**
 * Straight-line distance between two airports of the same metro, either way
 * round, or null for a pair that is not listed. Same table as the card chip,
 * so the filter rail and the card can never disagree about a number.
 */
export function airportDistanceKm(
  a: string | null | undefined,
  b: string | null | undefined,
): number | null {
  return findPair(a, b)?.distanceKm ?? null;
}

/** Approximate drive between the two, in minutes, or null for an unlisted pair. */
export function airportDriveMinutes(
  a: string | null | undefined,
  b: string | null | undefined,
): number | null {
  return findPair(a, b)?.driveMinutes ?? null;
}

/** "~60 min drive" / "~1 h 30 min drive". */
export function driveTimeLabel(minutes: number): string {
  if (minutes < 60) return `~${minutes} min drive`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `~${h} h drive` : `~${h} h ${m} min drive`;
}

/**
 * What to call an airport on screen.
 *
 * Our own name for the handful we keep one for; otherwise the supplier's, when
 * it is a real one and not a bare code; and "<City> Airport" when there is
 * neither, which is still more use on a filter row than three letters.
 */
export function airportDisplayName(
  code: string | null | undefined,
  supplierName: string | null | undefined,
  city: string | null | undefined,
): string {
  const iata = code?.trim().toUpperCase() ?? '';
  const known = AIRPORT_NAMES[iata];
  if (known) return known;
  const name = supplierName?.trim() ?? '';
  if (name && name.toUpperCase() !== iata) return name;
  const where = city?.trim();
  return where ? `${where} Airport` : iata;
}

/** One sentence for the review page: what to expect, and how far. */
export function differentAirportSentence(
  note: DifferentAirportNote,
  side: 'departs' | 'arrives',
): string {
  // Said as a fact about the airport, not as a correction of the search: a
  // traveller who picked "Mumbai (All Airports)" asked for exactly this, and
  // "not the airport you searched" would be wrong for them.
  const verb = side === 'departs' ? 'Departs from' : 'Arrives at';
  const where = `${note.airport} (${note.code})`;
  if (note.distanceKm !== null && note.from) {
    return `${verb} ${where}, about ${note.distanceKm} km from ${note.from} (${note.searchedCode}).`;
  }
  return `${verb} ${where}, a different airport from ${note.searchedCode}.`;
}

/** The chip on a result card. Short: the card already names the city. */
export function differentAirportChip(note: DifferentAirportNote): string {
  return note.distanceKm !== null && note.from
    ? `${note.distanceKm} km from ${note.from}`
    : `Not ${note.searchedCode}`;
}

/**
 * The origin / destination codes of each searched leg, off the results URL
 * query (`seg=FROM:TO:DATE`, one per leg). Empty when there is none.
 */
export function parseSearchedLegs(query: string | null | undefined): Array<{
  origin: string;
  destination: string;
}> {
  if (!query) return [];
  // Hand-parsed rather than URLSearchParams: this module ships in the shared
  // package, whose TS lib has no DOM globals.
  const decode = (v: string): string => {
    try {
      return decodeURIComponent(v.replace(/\+/g, ' '));
    } catch {
      return v;
    }
  };
  return query
    .replace(/^\?/, '')
    .split('&')
    .filter((pair) => pair.startsWith('seg='))
    .map((pair) => decode(pair.slice('seg='.length)).split(':'))
    .filter((parts) => parts.length >= 2 && parts[0] && parts[1])
    .map((parts) => ({
      origin: (parts[0] as string).toUpperCase(),
      destination: (parts[1] as string).toUpperCase(),
    }));
}
